import React, { useState, useEffect, useRef } from "react";
import PhoneGrillhouse from "../../assets/restaurant/grillhouse.png";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ChevronRight, Check } from "lucide-react";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import RestaurantCantina from "../../assets/restaurant/la-cantina-mac.png";

const primaryColor = "#43bd98";

const examples = [{ mobile: PhoneGrillhouse, web: RestaurantCantina }];

function NotificationCard({
	title,
	line1,
	line2,
	style,
	delay = 1,
	float = true,
	direction = "y",
	size = "sm",
}) {
	const lg = size === "lg";

	return (
		<motion.div
			style={{
				position: "absolute",
				zIndex: 40,
				pointerEvents: "none",
				...style,
			}}
			initial={{
				opacity: 0,
				scale: 0.9,
				...(direction === "x" ? { x: -24 } : { y: -24 }),
			}}
			animate={{
				opacity: 1,
				scale: 1,
				x: 0,
				y: float ? [0, -6, 0] : 0,
			}}
			transition={{
				opacity: { duration: 0.5, delay },
				scale: {
					duration: 0.5,
					delay,
					type: "spring",
					stiffness: 240,
					damping: 16,
				},
				x: { duration: 0.5, delay },
				y: float
					? {
							duration: 3.8,
							repeat: Infinity,
							ease: "easeInOut",
							delay: delay + 0.5,
						}
					: { duration: 0.5, delay },
			}}
		>
			<div
				style={{
					display: "flex",
					alignItems: "center",
					gap: "10px",
					borderRadius: "16px",
					padding: "8px 10px",
					background: "rgba(255,255,255,0.97)",
					border: "1px solid rgba(67,189,152,0.25)",
					boxShadow: "0 10px 24px rgba(0,0,0,0.12)",
					minWidth: "160px",
				}}
			>
				<div
					style={{
						width: lg ? "34px" : "32px",
						height: lg ? "34px" : "32px",
						borderRadius: "50%",
						flexShrink: 0,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						color: "#fff",
						fontSize: "12px",
						fontWeight: 700,
						background: "linear-gradient(135deg, #43bd98 0%, #2fae83 100%)",
					}}
				>
					{title.charAt(0)}
				</div>
				<div style={{ minWidth: 0, flex: 1 }}>
					<p
						style={{
							fontSize: lg ? "12px" : "11px",
							fontWeight: 700,
							lineHeight: 1.2,
							color: "#1a1a1a",
							margin: 0,
							whiteSpace: "nowrap",
							overflow: "hidden",
							textOverflow: "ellipsis",
						}}
					>
						{title}
					</p>
					<p
						style={{
							fontSize: lg ? "11px" : "10px",
							lineHeight: 1.2,
							color: "#555",
							margin: "2px 0 0",
							whiteSpace: "nowrap",
							overflow: "hidden",
							textOverflow: "ellipsis",
						}}
					>
						{line1}
					</p>
					<p
						style={{
							fontSize: lg ? "11px" : "10px",
							lineHeight: 1.2,
							color: "#999",
							margin: 0,
							whiteSpace: "nowrap",
							overflow: "hidden",
							textOverflow: "ellipsis",
						}}
					>
						{line2}
					</p>
				</div>
				<div
					style={{
						width: "22px",
						height: "22px",
						borderRadius: "50%",
						flexShrink: 0,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						background: "rgba(67,189,152,0.12)",
					}}
				>
					<Check size={12} color={primaryColor} strokeWidth={3} />
				</div>
			</div>
		</motion.div>
	);
}

const CTA_URL =
	"https://creator.applaby.com?onboarding=true&appType=Ecommerce&appSubtype=Restaurant";

// Barra de conversión persistente: aparece al dejar atrás la hero.
// Se mantiene oculta mientras el banner de cookies sigue en pantalla.
function StickyCta({ label }) {
	const [visible, setVisible] = useState(false);
	const barRef = useRef(null);

	// La barra flota sobre el documento, así que hay que reservarle sitio al
	// final o taparía lo último del footer (el selector de idioma)
	useEffect(() => {
		const bar = barRef.current;
		if (!bar) return undefined;
		const previous = document.body.style.paddingBottom;
		document.body.style.paddingBottom = `${bar.offsetHeight}px`;
		return () => {
			document.body.style.paddingBottom = previous;
		};
	}, []);

	useEffect(() => {
		const update = () => {
			const hero = document.getElementById("inicio");
			if (!hero) return;
			const pastHero =
				window.scrollY > hero.offsetTop + hero.offsetHeight - 120;
			setVisible(pastHero && localStorage.getItem("cookiesChoice") !== null);
		};
		update();
		window.addEventListener("scroll", update, { passive: true });
		return () => window.removeEventListener("scroll", update);
	}, []);

	return (
		<motion.div
			ref={barRef}
			initial={false}
			animate={{ y: visible ? 0 : 100, opacity: visible ? 1 : 0 }}
			transition={{ duration: 0.25, ease: "easeOut" }}
			style={{
				position: "fixed",
				left: 0,
				right: 0,
				bottom: 0,
				zIndex: 40,
				padding: "12px 24px calc(12px + env(safe-area-inset-bottom))",
				background: "rgba(255,255,255,0.96)",
				backdropFilter: "blur(8px)",
				borderTop: "1px solid #e5e7eb",
				pointerEvents: visible ? "auto" : "none",
			}}
		>
			<a
				href={CTA_URL}
				target="_blank"
				rel="noreferrer"
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					gap: "6px",
					height: "52px",
					borderRadius: "12px",
					background: "linear-gradient(135deg, #43bd98 0%, #2fae83 100%)",
					color: "#fff",
					fontWeight: 600,
					fontSize: "1rem",
					textDecoration: "none",
				}}
			>
				{label} <ChevronRight size={18} />
			</a>
		</motion.div>
	);
}

const Header = () => {
	const isMobile = useMediaQuery("(max-width: 550px)");
	const isMedium = useMediaQuery("(max-width: 1200px)");

	const [start, setStart] = useState(false);

	const { t } = useTranslation();

	useEffect(() => {
		const timer = setTimeout(() => setStart(true));
		return () => clearTimeout(timer);
	}, []);

	const current = examples[0];

	// Lista de beneficios rápidos
	const QuickBenefits = ({ mobile = false }) => (
		<motion.div
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			transition={{ delay: 0.5, duration: 0.5 }}
			style={{
				display: "flex",
				gap: mobile ? "12px" : "20px",
				marginTop: mobile ? "16px" : "20px",
				flexWrap: "wrap",
				justifyContent: mobile || isMedium ? "center" : "flex-start",
			}}
		>
			{t("restaurantHeader.quickBenefits", { returnObjects: true }).map(
				(benefit, i) => (
					<div
						key={i}
						style={{
							display: "flex",
							alignItems: "center",
							gap: "6px",
							fontSize: mobile ? "0.85rem" : "0.9rem",
							color: "#4b5563",
						}}
					>
						<Check
							size={mobile ? 14 : 16}
							color={primaryColor}
							strokeWidth={3}
						/>
						<span>{benefit}</span>
					</div>
				)
			)}
		</motion.div>
	);

	return (
		<>
			{isMobile ? (
				<section
					id="inicio"
					style={{
						position: "relative",
						overflow: "hidden",
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						textAlign: "center",
						fontFamily: "'Poppins', sans-serif",
						marginTop: "70px",
						backgroundColor: "#fff",
						paddingBottom: "24px",
					}}
				>
					{/* Bloque de mensaje: badge, titular, promesa y CTA */}
					<motion.div
						initial={{ opacity: 0, y: 16 }}
						animate={{
							opacity: 1,
							y: 0,
							transition: { duration: 0.4, ease: "easeOut" },
						}}
						style={{
							width: "100%",
							padding: "40px 24px 0",
						}}
					>
						{/* Badge de prueba gratis */}
						<motion.div
							initial={{ opacity: 0, scale: 0.9 }}
							animate={{ opacity: 1, scale: 1 }}
							transition={{ delay: 0.1 }}
							style={{
								display: "inline-block",
								background: "transparent",
								border: `1.5px solid ${primaryColor}`,
								color: primaryColor,
								padding: "6px 14px",
								borderRadius: "20px",
								fontSize: "0.8rem",
								fontWeight: 600,
							}}
						>
							{t("restaurantHeader.trialBadge")}
						</motion.div>

						{/* textWrap balance reparte el titular en líneas de ancho
						    parecido. Evita que quede una palabra suelta en la última
						    línea, que es lo que pasaba en catalán e inglés, donde el
						    texto destacado es bastante más largo que en español */}
						<h1
							style={{
								fontSize: "clamp(1.5rem, 7.9vw, 2rem)",
								fontWeight: 800,
								lineHeight: 1.15,
								color: "#111827",
								margin: "16px 0 0",
								textWrap: "balance",
							}}
						>
							{t("restaurantHeader.title")}{" "}
							<span style={{ color: primaryColor }}>
								{t("restaurantHeader.highlight")}
							</span>
						</h1>

						<p
							style={{
								fontSize: "1rem",
								lineHeight: 1.5,
								color: "#4b5563",
								margin: "12px 0 0",
							}}
						>
							{t("restaurantHeader.subtitle1")}
						</p>

						<motion.a
							href={CTA_URL}
							target="_blank"
							rel="noreferrer"
							whileTap={{ scale: 0.98 }}
							initial={{ opacity: 0, y: 10 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ delay: 0.15, duration: 0.4, ease: "easeOut" }}
							style={{
								background: "linear-gradient(135deg, #43bd98 0%, #2fae83 100%)",
								color: "#fff",
								height: "56px",
								borderRadius: "12px",
								fontWeight: 600,
								fontSize: "1rem",
								width: "100%",
								marginTop: "24px",
								boxShadow: "0 8px 20px rgba(67,189,152,0.25)",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								gap: "6px",
							}}
						>
							{t("restaurantHeader.ctaPrimary")} <ChevronRight size={18} />
						</motion.a>

						<p
							style={{
								fontSize: "0.8125rem",
								lineHeight: 1.4,
								color: "#6b7280",
								margin: "10px 0 0",
							}}
						>
							{t("restaurantHeader.ctaNote")}
						</p>
					</motion.div>

					{/* Mockup: mismos assets que en escritorio, recompuestos en
					    vertical. El portátil sangra por la derecha y el conjunto se
					    dimensiona para que el corte lo haga el borde del viewport */}
					<motion.div
						initial={{ opacity: 0, y: 16 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ delay: 0.25, duration: 0.5 }}
						style={{
							position: "relative",
							width: "100%",
							height: "360px",
							marginTop: "24px",
						}}
					>
						{/* Los dos dispositivos se anclan por abajo para que compartan
						    línea de suelo, y el teléfono monta sobre el portátil en
						    lugar de flotar a su lado */}
						<img
							src={current.web}
							alt="Web del restaurante La Cantina en un portátil"
							style={{
								position: "absolute",
								right: "-36px",
								bottom: 0,
								width: "400px",
								// Tailwind limita las imágenes al ancho del contenedor y
								// aquí necesitamos que el portátil sangre por la derecha
								maxWidth: "none",
								filter: "drop-shadow(0 12px 28px rgba(0,0,0,0.14))",
							}}
						/>
						<img
							src={current.mobile}
							alt="App del restaurante Grillhouse 27 con su carta digital"
							style={{
								position: "absolute",
								left: "16px",
								bottom: "24px",
								width: "200px",
								filter: "drop-shadow(0 16px 32px rgba(0,0,0,0.22))",
							}}
						/>
						<NotificationCard
							title={t("restaurantHeader.notifications.reservation.title")}
							line1={t("restaurantHeader.notifications.reservation.line1")}
							line2={t("restaurantHeader.notifications.reservation.line2")}
							delay={0.7}
							direction="x"
							size="lg"
							// Arriba a la derecha: ocupa el hueco que dejan los dos
							// dispositivos sin tapar la pantalla del portátil
							style={{ width: "230px", right: "40px", top: "24px" }}
						/>
					</motion.div>

					{/* Beneficios: una columna alineada a la izquierda para evitar
					    el escalonado que produce el wrap centrado */}
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 0.5 }}
						style={{
							display: "flex",
							justifyContent: "center",
							width: "100%",
							padding: "0 24px",
							marginTop: "28px",
						}}
					>
						<div style={{ display: "grid", gap: "10px", textAlign: "left" }}>
							{t("restaurantHeader.quickBenefits", {
								returnObjects: true,
							}).map((benefit, i) => (
								<div
									key={i}
									style={{
										display: "flex",
										alignItems: "center",
										gap: "8px",
										fontSize: "0.875rem",
										color: "#4b5563",
									}}
								>
									<Check
										size={16}
										color={primaryColor}
										strokeWidth={3}
										style={{ flexShrink: 0 }}
									/>
									<span>{benefit}</span>
								</div>
							))}
						</div>
					</motion.div>

					{/* Partner Gremi */}
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 0.6 }}
						style={{
							display: "flex",
							justifyContent: "center",
							width: "100%",
							padding: "0 24px",
							marginTop: "20px",
						}}
					>
						<a
							href="https://gremirestauracio.com/es/applaby-pa-87-es"
							target="_blank"
							rel="noreferrer"
							style={{
								display: "inline-flex",
								alignItems: "center",
								justifyContent: "center",
								minHeight: "44px",
								padding: "0 16px",
								fontSize: "0.75rem",
								color: "#6b7280",
								textDecoration: "none",
								border: "1px solid #e5e7eb",
								borderRadius: "22px",
							}}
						>
							{t("restaurantHeader.gremiPartner")}
						</a>
					</motion.div>

					<StickyCta label={t("restaurantHeader.ctaPrimary")} />
				</section>
			) : (
				<section
					className="section"
					id="inicio"
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						padding: "4rem 2rem",
						background: "#f8fefc",
						fontFamily: "'Poppins', sans-serif",
						marginTop: "80px",
					}}
				>
					<div
						className="header-container"
						style={{
							maxWidth: "1200px",
							width: "100%",
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							flexWrap: "wrap",
							gap: "2rem",
						}}
					>
						<motion.div
							className="text-block"
							style={{ flex: "1 1 500px" }}
							initial={{ opacity: 0, y: 30 }}
							animate={start ? { opacity: 1, y: 0 } : {}}
							transition={{ duration: 0.8 }}
						>
							{/* Badge de prueba gratis */}
							<motion.div
								initial={{ opacity: 0, scale: 0.9 }}
								animate={start ? { opacity: 1, scale: 1 } : {}}
								transition={{ delay: 0.1, duration: 0.5 }}
								style={{
									display: "inline-block",
									background:
										"linear-gradient(135deg, #43bd98 0%, #2fae83 100%)",
									color: "#fff",
									padding: "8px 18px",
									borderRadius: "24px",
									fontSize: "0.9rem",
									fontWeight: 600,
									marginBottom: "20px",
								}}
							>
								{t("restaurantHeader.trialBadge")}
							</motion.div>

							<motion.h1
								key={start}
								initial={{ opacity: 0, y: 20 }}
								animate={start ? { opacity: 1, y: 0 } : {}}
								transition={{ duration: 0.8, delay: 0.2 }}
								className="text-4xl md:text-5xl lg:text-[3.5rem] font-extrabold text-gray-900 leading-[1.1] mb-6"
							>
								{t("restaurantHeader.title")}{" "}
								<span style={{ color: primaryColor }}>
									{t("restaurantHeader.highlight")}
								</span>
							</motion.h1>

							<motion.p
								style={{
									fontSize: "1.05rem",
									lineHeight: "1.7",
									color: "rgb(75, 85, 99)",
									maxWidth: "520px",
									marginInline: isMedium ? "auto" : 0,
								}}
								initial={{ opacity: 0, y: 20 }}
								animate={start ? { opacity: 1, y: 0 } : {}}
								transition={{ duration: 0.8, delay: 0.4 }}
								className="fixed-paragraph"
							>
								{t("restaurantHeader.subtitle1")}
							</motion.p>

							<motion.p
								style={{
									fontSize: "1.05rem",
									lineHeight: "1.7",
									color: "#111827",
									fontWeight: 600,
									marginTop: "8px",
								}}
								initial={{ opacity: 0, y: 20 }}
								animate={start ? { opacity: 1, y: 0 } : {}}
								transition={{ duration: 0.8, delay: 0.45 }}
							>
								{t("restaurantHeader.subtitle2")}
							</motion.p>

							<QuickBenefits mobile={false} />

							{/* Partner Gremi */}
							<motion.div
								initial={{ opacity: 0 }}
								animate={start ? { opacity: 1 } : {}}
								transition={{ delay: 0.55, duration: 0.5 }}
								style={{
									marginTop: "14px",
									display: "flex",
									justifyContent: isMedium ? "center" : "flex-start",
								}}
							>
								<a
									href="https://gremirestauracio.com/es/applaby-pa-87-es"
									target="_blank"
									rel="noreferrer"
									style={{
										display: "inline-flex",
										alignItems: "center",
										gap: "6px",
										fontSize: "0.8rem",
										color: "#6b7280",
										textDecoration: "none",
										border: "1px solid #e5e7eb",
										borderRadius: "20px",
										padding: "6px 14px",
										transition: "border-color 0.3s ease",
									}}
								>
									{t("restaurantHeader.gremiPartner")}
								</a>
							</motion.div>

							<motion.div
								className="flex justify-center md:justify-start gap-4 flex-wrap"
								initial={{ opacity: 0, y: 10 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.8, ease: "easeOut", delay: 0.5 }}
								style={{
									marginTop: 20,
									display: "flex",
									justifyContent: isMedium ? "center" : "flex-start",
								}}
							>
								<a
									href="https://creator.applaby.com?onboarding=true&appType=Ecommerce&appSubtype=Restaurant"
									target="_blank"
									rel="noreferrer"
									className="px-8 py-3 rounded-xl font-semibold shadow-md hover:shadow-lg hover:scale-105 transition flex items-center gap-2"
									style={{
										background:
											"linear-gradient(135deg, #43bd98 0%, #33a884 100%)",
										color: "#fff",
									}}
								>
									{t("restaurantHeader.ctaPrimary")} <ChevronRight size={18} />
								</a>
							</motion.div>
						</motion.div>

						<div
							className="image-wrapper"
							style={{
								position: "relative",
								width: "100%",
								flex: "1 1 600px",
								height: "500px",
								marginBottom: "2rem",
								overflow: "visible",
								display: "flex",
								justifyContent: "center",
								alignItems: "flex-end",
							}}
						>
							<AnimatePresence mode="wait">
								<motion.img
									key={current.web}
									src={current.web}
									alt="Web"
									initial={{ opacity: 0, y: 20 }}
									animate={{ opacity: 1, y: 0 }}
									exit={{ opacity: 0, y: -20 }}
									transition={{ duration: 0.8 }}
									className="web"
									style={{
										position: "absolute",
										bottom: 0,
										width: "1000px",
										maxWidth: "none",
										borderRadius: "16px",
										zIndex: 1,
										left: "40px",
										top: "-25px",
									}}
								/>
							</AnimatePresence>

							<AnimatePresence mode="wait">
								<motion.img
									key={current.mobile}
									src={current.mobile}
									alt="App"
									initial={{ opacity: 0, y: 30 }}
									animate={{ opacity: 1, y: 0 }}
									exit={{ opacity: 0, y: -30 }}
									transition={{ duration: 0.8, delay: 0.2 }}
									className="mobile"
									style={{
										position: "absolute",
										width: "300px",
										borderRadius: "20px",
										zIndex: 2,
										left: "60px",
										top: "40px",
									}}
								/>
							</AnimatePresence>

							<NotificationCard
								title={t("restaurantHeader.notifications.order.title")}
								line1={t("restaurantHeader.notifications.order.line1")}
								line2={t("restaurantHeader.notifications.order.line2")}
								delay={1}
								style={{ width: "200px", right: "4%", top: "18px" }}
							/>
							<NotificationCard
								title={t("restaurantHeader.notifications.reservation.title")}
								line1={t("restaurantHeader.notifications.reservation.line1")}
								line2={t("restaurantHeader.notifications.reservation.line2")}
								delay={1.4}
								direction="x"
								style={{ width: "190px", left: "8px", top: "48%" }}
							/>
							<NotificationCard
								title={t("restaurantHeader.notifications.pickup.title")}
								line1={t("restaurantHeader.notifications.pickup.line1")}
								line2={t("restaurantHeader.notifications.pickup.line2")}
								delay={1.7}
								style={{ width: "200px", right: "2%", top: "52%" }}
							/>
						</div>
					</div>
				</section>
			)}

			<style>{`
				@media (max-width: 1200px) and (min-width: 600px) {
					.header-container {
						justify-content: center !important;
						text-align: center !important;
						gap: 48px;
					}
					.text-block {
						flex: 1 1 100% !important;
					}
					.image-wrapper {
						flex: 1 1 100% !important;
						justify-content: center !important;
					}
				}
				@media (max-width: 600px) {
					.section {
						padding: 40px 20px !important;
						overflow: hidden !important;
					}
					.header-container {
						flex-direction: column;
						text-align: center;
						gap: 32px;
					}
					.text-block {
						flex: 1 1 100% !important;
					}
					.image-wrapper {
						order: 2;
						height: 320px !important;
						margin-bottom: 16px;
						justify-content: center !important;
						align-items: center !important;
						overflow: hidden;
						flex: 1 1 auto !important;
					}
					#inicio h1 {
						margin-top: 12px;
						line-height: 1.3;
					}
					.cta-buttons {
						flex-direction: column;
						align-items: center;
						width: 100%;
					}
					.cta-buttons a {
						width: 100% !important;
						max-width: 320px;
						text-align: center;
						padding: 1rem 1.4rem !important;
						font-size: 1rem !important;
					}
					.image-wrapper img.web {
						width: 500px !important;
						left: 20px !important;
						top: -10px !important;
					}
					.image-wrapper img.mobile {
						width: 180px !important;
						left: -10px !important;
						top: 20px !important;
					}
				}
				@media (max-width: 480px) {
					#inicio h1 {
						line-height: 1.25;
					}
					.image-wrapper {
						height: 250px !important;
						flex: 1 1 250px !important;
					}
					.image-wrapper img.web {
						width: 380px !important;
						left: 0 !important;
						top: -5px !important;
					}
					.image-wrapper img.mobile {
						width: 150px !important;
						left: -5px !important;
						top: 15px !important;
					}
				}
			`}</style>
		</>
	);
};

export default Header;
